import { motion } from "framer-motion";
import ChapterHeader from "@/components/landing/ChapterHeader";
import { DAY_ORDER, JS_DAY_KEYS } from "@/lib/constants";
import { statusLabel } from "@/lib/status";
import { useLang } from "@/lib/i18n";

export default function Orari({ hours, status }) {
  const { t } = useLang();
  const todayKey = JS_DAY_KEYS[new Date().getDay()];

  return (
    <section id="orari" data-testid="orari-section" className="relative overflow-hidden py-28 sm:py-36">
      <div className="texture-paneling absolute inset-0" aria-hidden="true" />
      <div className="absolute inset-0 bg-ivory-light/90" aria-hidden="true" />
      <div className="relative mx-auto max-w-4xl px-5 sm:px-8">
        <ChapterHeader number="05" title={t.orari.title} subtitle={t.orari.subtitle} />

        {status && (
          <motion.div
            data-testid="orari-status-banner"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className={`mb-10 inline-flex items-center gap-3 rounded-full border px-6 py-3 ${
              status.open ? "border-emerald-600/30 bg-emerald-50" : "border-bordeaux/25 bg-bordeaux/5"
            }`}
          >
            <span className={`h-2.5 w-2.5 rounded-full ${status.open ? "bg-emerald-500 animate-pulse" : "bg-bordeaux"}`} />
            <span
              className={`font-mono-alt text-xs uppercase tracking-[0.22em] ${
                status.open ? "text-emerald-700" : "text-bordeaux"
              }`}
            >
              {statusLabel(t, status)}
            </span>
          </motion.div>
        )}

        <motion.div
          data-testid="orari-table"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="overflow-hidden rounded-3xl border border-bordeaux/15 bg-white shadow-[0_30px_80px_-40px_rgba(171,24,43,0.35)]"
        >
          {!hours ? (
            <p className="p-10 text-center font-mono-alt text-xs uppercase tracking-[0.25em] text-stone-400">
              {t.orari.loading}
            </p>
          ) : (
            DAY_ORDER.map((k) => {
              const d = hours.days?.[k];
              const isToday = k === todayKey;
              return (
                <div
                  key={k}
                  data-testid={`orari-row-${k}`}
                  className={`flex items-center justify-between px-6 py-5 sm:px-10 ${
                    isToday ? "bg-bordeaux/[0.06]" : ""
                  } border-b border-bordeaux/10 last:border-0`}
                >
                  <div className="flex items-center gap-3">
                    <span className={`font-display text-xl font-medium sm:text-2xl ${isToday ? "text-bordeaux" : "text-ink"}`}>
                      {t.orari.days[k]}
                    </span>
                    {isToday && (
                      <span className="rounded-full bg-bordeaux px-3 py-0.5 font-mono-alt text-[9px] uppercase tracking-[0.2em] text-ivory">
                        {t.orari.today}
                      </span>
                    )}
                  </div>
                  <span
                    className={`font-mono-alt text-sm tracking-[0.15em] ${
                      !d || d.closed ? "text-stone-400" : "text-stone-700"
                    }`}
                  >
                    {!d ? "—" : d.closed ? t.orari.closed : `${d.open} – ${d.close}`}
                  </span>
                </div>
              );
            })
          )}
        </motion.div>

        {hours?.force_closed && hours?.closing_note && (
          <p data-testid="orari-closing-note" className="mt-6 text-center font-display text-lg italic text-bordeaux-dark">
            {hours.closing_note}
          </p>
        )}
      </div>
    </section>
  );
}
